import _ from 'lodash';
import moment from 'moment';
import { useRef } from 'react';
import { KEY_RECOIL } from '@/shared/config';
import { convertToMoment } from '@/shared/lib';
import { useMergeState, useUpdateEffect } from '@/shared/lib/customHooks';
import { toastrError } from '@/shared/lib/toastNotification';
import { getDataRange } from '@/shared/lib/timeRange';
import { TPickDateAction } from '@/shared/model';
import {
  DEFAULT_CONTROL_MODAL_VALUE,
  selectPickedBookmarkDate,
  useReportStore,
} from '@/entities/report';
import type { EcgBookmarkTimeProps } from './ecgBookmarkTime';

/**
 * Drives the add-ECG-bookmark modal, letting a reviewer pick a moment on the strip, annotate it and save it as a bookmark. Mirrors the picked bookmark date into the date and time fields, validates both before saving, refuses times that fall in a no-data gap of the ECG map with a toast, then stores a bookmark spanning five seconds either side of the picked moment plus its notes and closes the modal.
 *
 * @param props - Modal configuration supplying the ECG data map used for the no-data check and the timezone for display.
 * @returns The form state, the date, notes and key-down handlers, the jump-to, pick-on, cancel and add-bookmark actions, and the jump and pick time refs.
 */
const useEcgBookmarkTime = ({ ecgDataMap, timezone }: EcgBookmarkTimeProps) => {
  const keyRecoil = KEY_RECOIL.HR_SUMMARY;

  const setJumpDateAction = useReportStore((s) => s.actions.setJumpDate);
  /**
   * Moves the ECG viewer to a given moment in the HR summary tab. Forwards the value to the shared jump-date action, binding it to the tab-6 store key.
   *
   * @param v - The target date, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setJumpDate = (v: Parameters<typeof setJumpDateAction>[1]) =>
    setJumpDateAction(keyRecoil, v);
  const setJumpPortionAction = useReportStore((s) => s.actions.setJumpPortion);
  /**
   * Selects which portion of the jumped-to page the ECG viewer scrolls to. Forwards the value to the shared jump-portion action, binding it to the tab-6 store key.
   *
   * @param v - The portion index, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setJumpPortion = (v: Parameters<typeof setJumpPortionAction>[1]) =>
    setJumpPortionAction(keyRecoil, v);
  const setPickDateActionAction = useReportStore((s) => s.actions.setPickDateAction);
  /**
   * Sets what a click on the ECG strip is currently picking, such as a bookmark moment. Forwards the value to the shared pick-date-action, binding it to the tab-6 store key.
   *
   * @param v - The pick-date mode, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setPickDateAction = (v: Parameters<typeof setPickDateActionAction>[1]) =>
    setPickDateActionAction(keyRecoil, v);
  const setControlModalAction = useReportStore((s) => s.actions.setControlModal);
  /**
   * Replaces the control-modal state of the HR summary tab. Forwards the value to the shared control-modal action, binding it to the tab-6 store key.
   *
   * @param v - The control-modal state, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setControlModal = (v: Parameters<typeof setControlModalAction>[1]) =>
    setControlModalAction(keyRecoil, v);
  const pickedBookmarkDate = useReportStore(selectPickedBookmarkDate(keyRecoil));
  const setEcgBookmarks = useReportStore((s) => s.actions.setEcgBookmarks);

  const [state, setState] = useMergeState<{
    date?: any;
    time?: any;
    errorObject: { date?: string; time?: string };
    notes: string;
  }>({
    date: undefined,
    time: undefined,
    errorObject: {},
    notes: '',
  });

  const jumpTimeRef = useRef<any>();
  const pickTimeRef = useRef<any>();

  /**
   * Records the date or time the reviewer picked for a new ECG bookmark. Writes the moment under the given field name into the merged form state and clears any outstanding validation errors.
   *
   * @param name - The form field being edited, either date or time.
   * @param time - The picked moment.
   * @param timeString - The picker's formatted text; accepted but unused.
   * @returns Nothing.
   */
  const onChange = (name: string, time: any, timeString: any) => {
    setState({ [name]: time, errorObject: {} });
  };

  /**
   * Moves the ECG viewer to the moment shown in the bookmark form. Converts the date to epoch milliseconds and writes it only when it differs from the current jump date, then selects the third page portion.
   *
   * @param date - The moment to jump to.
   * @returns Nothing.
   */
  const onClickJumpTo = (date: any) => {
    const epochDate = new Date(date).getTime();
    setJumpDate((prev: any) => {
      if (epochDate && epochDate !== prev) {
        return epochDate;
      }
      return prev;
    });
    setJumpPortion(2);
  };

  /**
   * Arms the ECG strip so the reviewer's next click picks the bookmark moment. Sets the pick-date action to the bookmark mode.
   *
   * @returns Nothing.
   */
  const onClickPickOn = () => {
    setPickDateAction(TPickDateAction.BOOKMARK);
  };

  /**
   * Records the free-text note attached to a new ECG bookmark. Writes the value under the given field name into the merged form state and clears any outstanding validation errors.
   *
   * @param name - The form field being edited.
   * @param value - The note text entered by the reviewer.
   * @returns Nothing.
   */
  const onChangeNotes = (name: string, value: any) => {
    setState({ [name]: value, errorObject: {} });
  };

  /**
   * Keeps typing inside the bookmark form from reaching the ECG viewer's keyboard shortcuts. Stops propagation of the key event and does nothing else.
   *
   * @param e - The keyboard event raised by the input.
   * @returns Nothing.
   */
  const onKeyDownInput = (e: any) => {
    e.stopPropagation();
  };

  /**
   * Abandons the bookmark being composed and closes the modal. Clears the validation errors and notes, then writes the default control-modal state with the add-bookmark panel hidden.
   *
   * @returns Nothing.
   */
  const onClickCancel = () => {
    setState({ errorObject: {}, notes: '' });
    setControlModal({ ...DEFAULT_CONTROL_MODAL_VALUE, isShowAddEcgBookmark: false });
  };

  /**
   * Validates the bookmark form and appends the new ECG bookmark to the report. Flags a missing date or time as invalid and stops, rejects a moment that falls outside any data range in the ECG map with an error toast, otherwise pushes a bookmark spanning five seconds either side of the picked moment with its notes and closes the modal.
   *
   * @returns Nothing.
   */
  const onClickAddBookmark = () => {
    const clonedErrorObject = { ...state.errorObject };
    if (!state.date) {
      _.assign(clonedErrorObject, { date: 'Invalid value' });
    } else {
      delete clonedErrorObject.date;
    }
    if (!state.time) {
      _.assign(clonedErrorObject, { time: 'Invalid value' });
    } else {
      delete clonedErrorObject.time;
    }
    if (!_.isEmpty(clonedErrorObject)) {
      setState({ errorObject: clonedErrorObject });
      return;
    }
    const pickedBookmarkDateConvert = moment(pickedBookmarkDate);
    const dataRange = getDataRange(
      ecgDataMap,
      pickedBookmarkDateConvert,
      pickedBookmarkDateConvert,
    );
    if (_.isEmpty(dataRange)) {
      toastrError('It is not possible to bookmark time in no data area.', 'Error');
      const clonedErrorObject = { ...state.errorObject };
      _.assign(clonedErrorObject, {
        time: 'Invalid value',
        date: 'Invalid value',
      });
      setState({ errorObject: clonedErrorObject });
      return;
    }
    const bookmark = {
      start: pickedBookmarkDateConvert.clone().add(-5, 'seconds').toISOString(),
      stop: pickedBookmarkDateConvert.clone().add(5, 'seconds').toISOString(),
      center: pickedBookmarkDateConvert.valueOf(), // pre-existing bug: IEcgBookmark.center is typed string, this is a number
      notes: state.notes,
    };
    setEcgBookmarks((prev) => [...prev, bookmark] as any);
    onClickCancel();
  };

  useUpdateEffect(() => {
    let convertedDateMoment;
    if (pickedBookmarkDate) {
      convertedDateMoment = convertToMoment(pickedBookmarkDate, timezone);
    }
    setState({
      date: convertedDateMoment,
      time: convertedDateMoment,
      errorObject: {},
    });
  }, [pickedBookmarkDate]);

  return {
    state,
    onChange,
    onClickJumpTo,
    onClickPickOn,
    onChangeNotes,
    onKeyDownInput,
    onClickCancel,
    onClickAddBookmark,
    jumpTimeRef,
    pickTimeRef,
  };
};

export default useEcgBookmarkTime;
